import React, { RefObject, useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import classnames from "classnames";
import { useRouter } from "next/router";

import { getPlatformStatI18nLabels } from "@vahak/core/dist/constants/platformStats";
import { MarketPlaceType } from "@vahak/core/dist/constants/market";
import { LocationSearchSource } from "@vahak/core/dist/_services/city-search.service";
import { WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { PlaceSearchResult } from "@vahak/core/dist/custom-types/location-search";
import VahakLogoBlue from "@vahak/core/dist/icons/VahakLogoBlue_n.svg";
import VahakLogoWhite from "@vahak/core/dist/icons/VahakLogoWhite_n.svg";

import PageMetaTags from "../common/page-meta-tags/PageMetaTags";
import { useCompactLoadPostingOptional } from "../homepage-v2/compact-load-posting/CompactLoadPostingProvider";
import QuoteFormLocationField from "../homepage-v2/hero-section/quote-form-card/fields/QuoteFormLocationField";
import QuoteFieldStyles from "../homepage-v2/hero-section/quote-form-card/QuoteFormFields.module.scss";
import { getHomeUrl } from "../../methods/pageSeo";

import LcvRouteIcon from "../lcv-route-landing/LcvRouteIcon";
import { CityPageChip, CityPageConfig, CityPageFaq, CityPageLink, CityPageLocalizedContent } from "./data/types";
import { getCityPagePath } from "./data";
import { LCV_ROUTE_PLAY_STORE_URL, LCV_ROUTE_SOCIAL_LINKS } from "../lcv-route-landing/lcvRouteLanding.constants";
import Styles from "../lcv-route-landing/LcvRouteLandingPage.module.scss";
import useLcvRouteLandingTranslation from "../lcv-route-landing/useLcvRouteLandingTranslation";
import { useMarketplaceV3PageSeo } from "../service-pages/marketplace/marketplace-v3/useMarketplaceV3PageSeo";

interface LcvRouteFooterGroup {
    label: string;
    links?: CityPageLink[];
    textItems?: string[];
    moreLink?: CityPageLink;
}

interface CityLandingPageProps {
    cityConfig?: CityPageConfig;
    marketplaceLorry?: {
        urlSrc: string;
        urlDest: string;
    };
}

const RouteLink = ({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) => {
    return (
        <Link href={href} className={className}>
            {children}
        </Link>
    );
};

const Header = () => {
    return (
        <header className={Styles["site-header"]}>
            <RouteLink href="/" className={Styles["logo-link"]}>
                <VahakLogoBlue className={Styles["logo-svg"]} aria-label="Vahak" />
            </RouteLink>
        </header>
    );
};

const DROP_FIELD_ICON = <LcvRouteIcon iconClassName="ti ti-navigation" />;
const PICKUP_FIELD_ICON = <LcvRouteIcon iconClassName="ti ti-map-pin" />;

const Hero = ({
    cityContent,
    formRef,
    heroCtaRef,
    onBookLorryNow,
    isCompactPostingOpen,
    destination,
    onDestinationChange,
    source,
    onSourceChange,
    bothFieldsEditable = false,
    eyebrow,
    headline1,
    ctaLabel,
    priceEstimatePrompt,
    priceEstimateLink,
    pickupAriaLabel,
    pickupPlaceholder
}: {
    cityContent: CityPageLocalizedContent;
    formRef: RefObject<HTMLFormElement | null>;
    heroCtaRef: RefObject<HTMLButtonElement | null>;
    onBookLorryNow: () => void;
    isCompactPostingOpen: boolean;
    destination?: PlaceSearchResult;
    onDestinationChange: (destination?: PlaceSearchResult) => void;
    source?: PlaceSearchResult;
    onSourceChange?: (source?: PlaceSearchResult) => void;
    bothFieldsEditable?: boolean;
    eyebrow: string;
    headline1: string;
    ctaLabel: string;
    priceEstimatePrompt: string;
    priceEstimateLink: string;
    pickupAriaLabel: string;
    pickupPlaceholder?: string;
}) => {
    const openPosting = () => {
        if (isCompactPostingOpen) {
            return;
        }
        onBookLorryNow();
    };

    return (
        <section className={Styles.hero}>
            <div className={Styles["hero-copy"]}>
                <p className={Styles["eyebrow-kw"]}>{eyebrow}</p>
                <h1 className={Styles["hero-h1"]}>
                    <span className={Styles["headline-1"]}>{headline1}</span>
                    {cityContent.heroHeadline2Lines.map((line) => (
                        <span className={Styles["headline-2"]} key={line}>
                            {line}
                        </span>
                    ))}
                </h1>
                <p className={Styles["hero-sub"]}>{cityContent.heroSub}</p>
            </div>

            <div className={Styles["hero-form-card"]}>
                <form
                    className={Styles["load-form"]}
                    id="load-form"
                    ref={formRef}
                    onSubmit={(event) => {
                        event.preventDefault();
                        openPosting();
                    }}
                >
                    {bothFieldsEditable ? (
                        <div className={classnames(QuoteFieldStyles.root, Styles.pickupLocationField)}>
                            <QuoteFormLocationField
                                id="lorry-pickup"
                                name="pickup"
                                placeholder={pickupPlaceholder ?? cityContent.dropPlaceholder}
                                icon={PICKUP_FIELD_ICON}
                                value={source}
                                onChange={onSourceChange ?? (() => undefined)}
                                locationSearchSource={LocationSearchSource.HomepageV2LoadSourcePosting}
                                fieldRole="pickup"
                                portalMenu
                            />
                        </div>
                    ) : (
                        <div className={Styles.field}>
                            <LcvRouteIcon iconClassName="ti ti-map-pin" />
                            <input
                                type="text"
                                name="pickup"
                                id="pickup"
                                defaultValue={cityContent.pickupCity}
                                aria-label={pickupAriaLabel}
                                readOnly
                            />
                        </div>
                    )}
                    <div className={classnames(QuoteFieldStyles.root, Styles.dropLocationField)}>
                        <QuoteFormLocationField
                            id="city-drop"
                            name="drop"
                            placeholder={cityContent.dropPlaceholder}
                            icon={DROP_FIELD_ICON}
                            value={destination}
                            onChange={onDestinationChange}
                            locationSearchSource={LocationSearchSource.HomepageV2LoadDestinationPosting}
                            fieldRole="drop"
                            portalMenu={bothFieldsEditable}
                        />
                    </div>
                    <div className={Styles["hero-actions"]}>
                        <button
                            type="submit"
                            className={Styles["btn-primary"]}
                            id="hero-cta-btn"
                            ref={heroCtaRef}
                            disabled={isCompactPostingOpen}
                        >
                            {ctaLabel}
                        </button>
                        <p className={Styles["price-link"]}>
                            {priceEstimatePrompt}{" "}
                            <a
                                href="#"
                                onClick={(event) => {
                                    event.preventDefault();
                                    openPosting();
                                }}
                                aria-disabled={isCompactPostingOpen}
                            >
                                {priceEstimateLink}
                            </a>
                        </p>
                    </div>
                </form>
            </div>
        </section>
    );
};

const TrustStrip = ({ text }: { text: string }) => {
    return (
        <div className={Styles["trust-strip"]}>
            <LcvRouteIcon iconClassName="ti ti-shield-check" />
            <span>{text}</span>
        </div>
    );
};

const HowItWorks = ({ title, steps }: { title: string; steps: string[] }) => {
    return (
        <div className={Styles["steps-block"]}>
            <h2 className={Styles["steps-title"]}>{title}</h2>
            {steps.map((text, index) => (
                <div className={Styles["step-row"]} key={text}>
                    <span className={Styles["step-num"]}>{index + 1}</span>
                    <span className={Styles["step-text"]}>{text}</span>
                </div>
            ))}
        </div>
    );
};

const AboutBlock = ({ title, aboutText, tags }: { title: string; aboutText: string; tags: string[] }) => {
    return (
        <div className={Styles["about-block"]}>
            <h2 className={Styles["about-title"]}>{title}</h2>
            <p className={Styles["about-text"]}>{aboutText}</p>
            {tags.length > 0 ? (
                <div className={Styles["tag-row"]}>
                    {tags.map((tag) => (
                        <span className={Styles.tag} key={tag}>
                            {tag}
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const ProofModule = ({
    proofLabel,
    stats,
    features
}: {
    proofLabel: string;
    stats: Array<{ value: string; label: string }>;
    features: Array<{ iconClassName: string; label: string }>;
}) => {
    return (
        <>
            <div className={Styles["proof-card"]}>
                <p className={Styles["proof-label"]}>{proofLabel}</p>
                <div className={classnames(Styles["stat-grid"], Styles["cols-2"])}>
                    {stats.map((item) => (
                        <div className={Styles["stat-cell"]} key={item.label}>
                            <p className={Styles["stat-num"]}>{item.value}</p>
                            <p className={Styles["stat-label"]}>{item.label}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className={Styles["feature-row"]}>
                {features.map((item) => (
                    <div className={Styles["feature-item"]} key={item.label}>
                        <LcvRouteIcon iconClassName={item.iconClassName} />
                        <span>{item.label}</span>
                    </div>
                ))}
            </div>
        </>
    );
};

const LocalResources = ({ title, dhabas, contacts, moreToggle, note }: CityPageLocalizedContent["localResources"]) => {
    return (
        <div className={Styles["local-block"]}>
            <h2 className={Styles["block-title"]}>{title}</h2>
            {dhabas.length > 0 ? (
                <div className={Styles["dhaba-row"]}>
                    {dhabas.map((dhaba) => (
                        <div className={Styles["dhaba-card"]} key={`${dhaba.name}-${dhaba.location}`}>
                            <p className={Styles.name}>{dhaba.name}</p>
                            <p className={Styles.loc}>{dhaba.location}</p>
                        </div>
                    ))}
                </div>
            ) : null}
            <div>
                {contacts.map((contact) => (
                    <div className={Styles["contact-row"]} key={`${contact.label}-${contact.number}`}>
                        <span>{contact.label}</span>
                        <span className={Styles.num}>{contact.number}</span>
                    </div>
                ))}
            </div>
            <details className={Styles["more-toggle"]}>
                <summary>{moreToggle}</summary>
            </details>
            <p className={Styles["local-note"]}>{note}</p>
        </div>
    );
};

const ChipBlock = ({ label, chips }: { label: string; chips: CityPageChip[] }) => {
    return (
        <div className={Styles["chip-block"]}>
            <p className={Styles["chip-label"]}>{label}</p>
            <div className={Styles["chip-row"]}>
                {chips.map((chip) =>
                    chip.href ? (
                        <RouteLink className={Styles.chip} href={chip.href} key={chip.label}>
                            {chip.label}
                        </RouteLink>
                    ) : (
                        <span className={Styles.chip} key={chip.label}>
                            {chip.label}
                        </span>
                    )
                )}
            </div>
        </div>
    );
};

const SeoContent = ({ cityContent, faqTitle }: { cityContent: CityPageLocalizedContent; faqTitle: string }) => {
    return (
        <>
            <div className={Styles["faq-block"]}>
                <h2 className={Styles["block-title"]}>{faqTitle}</h2>

                {cityContent.faqs.map((faq: CityPageFaq) => (
                    <details className={Styles["faq-item"]} open={faq.defaultOpen} key={faq.question}>
                        <summary>{faq.question}</summary>
                        <p className={Styles["faq-answer"]}>{faq.answer}</p>
                    </details>
                ))}
            </div>

            <ChipBlock label={cityContent.popularRoutesLabel} chips={cityContent.popularRoutes} />
            <ChipBlock label={cityContent.vehicleTypesLabel} chips={cityContent.vehicleTypes} />
        </>
    );
};

const FooterGroup = ({ group }: { group: LcvRouteFooterGroup }) => {
    return (
        <div className={Styles["footer-group"]}>
            <p className={Styles["fg-label"]}>{group.label}</p>
            <div className={Styles["fg-links"]}>
                {group.links?.map((link: CityPageLink) => (
                    <RouteLink href={link.href} key={link.href}>
                        {link.label}
                    </RouteLink>
                ))}
                {group.textItems?.map((item) => (
                    <span className={Styles["fg-text"]} key={item}>
                        {item}
                    </span>
                ))}
                {group.moreLink ? (
                    <RouteLink className={Styles["fg-more"]} href={group.moreLink.href}>
                        {group.moreLink.label}
                    </RouteLink>
                ) : null}
            </div>
        </div>
    );
};

const Footer = ({
    footerSeoText,
    brandText,
    groups,
    playStoreLabel,
    copyright,
    madeInIndia
}: {
    footerSeoText: string;
    brandText: string;
    groups: LcvRouteFooterGroup[];
    playStoreLabel: string;
    copyright: string;
    madeInIndia: string;
}) => {
    return (
        <footer className={Styles["site-footer"]}>
            <div className={Styles["footer-seo-text"]}>
                <p>{footerSeoText}</p>
            </div>

            <div className={Styles["wrap-footer"]}>
                <div className={Styles["footer-brand"]}>
                    <div className={Styles["logo-row"]}>
                        <RouteLink href="/" className={Styles["logo-link"]}>
                            <VahakLogoWhite className={Styles["footer-logo-svg"]} aria-label="Vahak" />
                        </RouteLink>
                    </div>
                    <p>{brandText}</p>
                </div>

                {groups.map((group) => (
                    <FooterGroup group={group} key={group.label} />
                ))}
            </div>

            <div className={Styles["footer-social"]}>
                {LCV_ROUTE_SOCIAL_LINKS.map((socialLink) => (
                    <a
                        key={socialLink.href}
                        href={socialLink.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={socialLink.label}
                    >
                        <LcvRouteIcon iconClassName={socialLink.iconClassName} />
                    </a>
                ))}
            </div>

            <div className={Styles["footer-app"]}>
                <a href={LCV_ROUTE_PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                    <LcvRouteIcon iconClassName="ti ti-brand-google-play" />
                    <span>{playStoreLabel}</span>
                </a>
            </div>

            <div className={Styles["footer-bottom"]}>
                <p>{copyright}</p>
                <span className={Styles["footer-made"]}>{madeInIndia}</span>
            </div>
        </footer>
    );
};

const StickyCta = ({
    isVisible,
    onClick,
    isCompactPostingOpen,
    ctaLabel
}: {
    isVisible: boolean;
    onClick: () => void;
    isCompactPostingOpen: boolean;
    ctaLabel: string;
}) => {
    return (
        <div className={classnames(Styles["sticky-cta"], isVisible && Styles["is-visible"])}>
            <button type="button" className={Styles["btn-primary"]} onClick={onClick} disabled={isCompactPostingOpen}>
                {ctaLabel}
            </button>
        </div>
    );
};

const CityLandingPage = ({ cityConfig, marketplaceLorry }: CityLandingPageProps) => {
    const isMarketplaceLorry = Boolean(marketplaceLorry);
    const { t } = useLcvRouteLandingTranslation();
    const { locale = "en" } = useRouter();
    const compactPosting = useCompactLoadPostingOptional();
    const formRef = useRef<HTMLFormElement>(null);
    const heroCtaRef = useRef<HTMLButtonElement>(null);
    const [isStickyCtaVisible, setIsStickyCtaVisible] = useState(false);
    const [destination, setDestination] = useState<PlaceSearchResult>();
    const [source, setSource] = useState<PlaceSearchResult>();
    const isCompactPostingOpen = Boolean(compactPosting?.isCompactPostingOpen);

    const marketplaceSeo = useMarketplaceV3PageSeo(
        marketplaceLorry?.urlSrc ?? "",
        marketplaceLorry?.urlDest ?? "",
        isMarketplaceLorry ? MarketPlaceType.LORRY : MarketPlaceType.LOAD
    );

    const cityContent = (
        isMarketplaceLorry ? marketplaceSeo.landingContent : t(`cities.${cityConfig!.slug}`, { returnObjects: true })
    ) as CityPageLocalizedContent;
    const howItWorksSteps = t("howItWorks.steps", { returnObjects: true }) as string[];
    const features = t("features", { returnObjects: true }) as Array<{ iconClassName: string; label: string }>;
    const footerGroups = (
        cityContent.footerGroups?.length
            ? cityContent.footerGroups
            : (t("footer.groups", { returnObjects: true }) as LcvRouteFooterGroup[])
    ) as LcvRouteFooterGroup[];
    const faqList = cityContent.faqs.map((faq) => ({
        question: faq.question,
        answer: faq.answer
    }));

    const isKannada = locale === "kn";
    const isHindi = locale === "hi" || locale === "mr";
    const isTelugu = locale === "te";
    const isGujarati = locale === "gu";
    const isBengali = locale === "bn";
    const isTamil = locale === "ta";

    const proofStats = cityContent.proofStats.length
        ? cityContent.proofStats
        : (t("proof.stats", { returnObjects: true }) as Array<{ value: string; label: string }>);
    const trustStripText = cityContent.trustStrip || t("trustStrip", getPlatformStatI18nLabels());
    const postingSourceScreen = isMarketplaceLorry
        ? WEB_V2_SOURCE_SCREENS.MARKETPLACE
        : WEB_V2_SOURCE_SCREENS.LCV_CITY_LANDING;

    const handleBookLorryNow = useCallback(() => {
        compactPosting?.openCompactPosting(
            isMarketplaceLorry
                ? {
                      ...(source ? { source } : {}),
                      ...(destination ? { destination } : {})
                  }
                : {
                      source: cityConfig!.city,
                      ...(destination ? { destination } : {})
                  },
            {
                eventData: { source_screen: postingSourceScreen }
            }
        );
    }, [cityConfig, compactPosting, destination, isMarketplaceLorry, postingSourceScreen, source]);

    useEffect(() => {
        const heroBtn = heroCtaRef.current;

        if (!heroBtn || typeof IntersectionObserver === "undefined") {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    setIsStickyCtaVisible(!entry.isIntersecting);
                });
            },
            { threshold: 0 }
        );

        observer.observe(heroBtn);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <>
            <PageMetaTags
                {...(isMarketplaceLorry
                    ? {
                          asPath: marketplaceSeo.asPath,
                          localeNeutralPath: marketplaceSeo.marketplaceSeoPath,
                          title: marketplaceSeo.pageMeta.title,
                          description: marketplaceSeo.pageMeta.description,
                          keywords: marketplaceSeo.pageMeta.keywords,
                          ogImgAltText: marketplaceSeo.pageMeta.ogImgAltText,
                          shouldBeIndexed: marketplaceSeo.shouldBeIndexed,
                          schema: {
                              breadcrumbItems: marketplaceSeo.breadcrumbItems,
                              faqList: marketplaceSeo.faqList
                          }
                      }
                    : {
                          title: cityContent.seo.title,
                          description: cityContent.seo.description,
                          locale,
                          localeNeutralPath: getCityPagePath(cityConfig!.slug),
                          hreflangLocales: cityConfig!.hreflangLocales,
                          ogType: "website" as const,
                          ogSiteName: "Vahak",
                          ogImgAltText: cityContent.seo.title,
                          includeDefaultSchema: true,
                          schema: {
                              breadcrumbItems: [
                                  { name: t("meta.breadcrumbHome"), url: getHomeUrl() },
                                  { name: t("meta.breadcrumbLcvBooking"), path: "/lcv-booking" },
                                  { name: cityContent.breadcrumbCityName }
                              ],
                              faqList
                          }
                      })}
            />
            <main
                className={classnames(
                    Styles.page,
                    isKannada && Styles.pageKn,
                    isHindi && Styles.pageHi,
                    isTelugu && Styles.pageTe,
                    isGujarati && Styles.pageGu,
                    isBengali && Styles.pageBn,
                    isTamil && Styles.pageTa
                )}
                lang={locale}
            >
                <Header />
                <Hero
                    cityContent={cityContent}
                    formRef={formRef}
                    heroCtaRef={heroCtaRef}
                    onBookLorryNow={handleBookLorryNow}
                    isCompactPostingOpen={isCompactPostingOpen}
                    destination={destination}
                    onDestinationChange={setDestination}
                    source={source}
                    onSourceChange={setSource}
                    bothFieldsEditable={isMarketplaceLorry}
                    eyebrow={cityContent.heroEyebrow ?? t("hero.eyebrow")}
                    headline1={cityContent.heroHeadline1 ?? t("hero.headline1")}
                    ctaLabel={t("hero.cta")}
                    priceEstimatePrompt={t("hero.priceEstimatePrompt")}
                    priceEstimateLink={t("hero.priceEstimateLink")}
                    pickupAriaLabel={t("hero.pickupAriaLabel")}
                    pickupPlaceholder={cityContent.pickupPlaceholder}
                />
                <TrustStrip text={trustStripText} />
                <HowItWorks title={t("howItWorks.title")} steps={howItWorksSteps} />
                <AboutBlock title={cityContent.aboutTitle} aboutText={cityContent.aboutText} tags={cityContent.tags} />
                <ProofModule proofLabel={t("proof.label")} stats={proofStats} features={features} />
                {!isMarketplaceLorry ? <LocalResources {...cityContent.localResources} /> : null}
                {isMarketplaceLorry ? (
                    <div className={Styles["faq-block"]}>
                        <h2 className={Styles["block-title"]}>{t("faq.title")}</h2>
                        {cityContent.faqs.map((faq: CityPageFaq) => (
                            <details className={Styles["faq-item"]} key={faq.question}>
                                <summary>{faq.question}</summary>
                                <p className={Styles["faq-answer"]}>{faq.answer}</p>
                            </details>
                        ))}
                    </div>
                ) : (
                    <SeoContent cityContent={cityContent} faqTitle={t("faq.title")} />
                )}
                <Footer
                    footerSeoText={cityContent.footerSeoText}
                    brandText={t("footer.brandText")}
                    groups={footerGroups}
                    playStoreLabel={t("footer.playStore")}
                    copyright={t("footer.copyright")}
                    madeInIndia={t("footer.madeInIndia")}
                />
                <StickyCta
                    isVisible={isStickyCtaVisible}
                    onClick={handleBookLorryNow}
                    isCompactPostingOpen={isCompactPostingOpen}
                    ctaLabel={t("hero.cta")}
                />
            </main>
        </>
    );
};

export default CityLandingPage;
